'use client';
import { Dialog, DialogPanel } from '@headlessui/react';
import clsx from 'clsx';
import { useState } from 'react';
import { IoCloseSharp } from 'react-icons/io5';
import { Else, If, Then, When } from 'react-if';

import cn from '@/lib/clsxm';

import { SIZE_ENUM } from '@/components/@base/@helpers/types';
import Button from '@/components/@base/button';
import Container from '@/components/@base/container';
import Loading from '@/components/@base/loading';
import { LOADING_COLORS } from '@/components/@base/loading/type';
import { Text } from '@/components/@base/typography';
import XImage from '@/components/@base/x-image';
import PriceView from '@/components/shared/components/price-view';
import { useDelayedVisibilityOnFocus } from '@/components/shared/components/smart-offers/hooks/useDelayedVisibilityOnFocus';
import { useCloseSmartOffer } from '@/components/shared/components/smart-offers/services/hooks';
import useAddToCartHandler from '@/components/shared/helpers/hooks/use-add-to-cart-handler';

import localization from '@/constant/localization';
import SmartOfferModel from '@/models/smart-offer.model';

type Props = {
  data: SmartOfferModel;
};
export default function SmartPopUP({ data }: Props) {
  const { handleAddToCart, isAddingToCart } = useAddToCartHandler();
  const isVisible = useDelayedVisibilityOnFocus(data?.displayDelay ?? 500);
  const [isClosedManually, setIsClosedManually] = useState(false);
  const { mutate, isPending } = useCloseSmartOffer();
  const isOpen = isVisible && !isClosedManually;
  if (isAddingToCart) {
    return (
      <Container className='w-[36px] pl-2'>
        <Loading color={LOADING_COLORS.ORANGE} />
      </Container>
    );
  }
  const handleAddToCartClick = () => {
    setIsClosedManually(true);
    handleAddToCart({ data });
  };
  const handleClose = () => {
    setIsClosedManually(true);
    mutate(data?.id, {
      onError: (e) => {},
      onSuccess: (res) => {},
    });
  };
  const previewLink =
    data?.productType === 'addon'
      ? `/web/${data?.product?.targetSlug}`
      : `/web/${data?.product?.slug}`;
  return (
    <>
      <Dialog
        open={isOpen}
        onClose={() => setIsClosedManually(true)}
        className='relative z-100 md:w-[363px]'
      >
        <div className='fixed inset-0 bg-black/50' onClick={(e) => e.preventDefault()} />
        <div className='fixed inset-0 overflow-y-auto'>
          <div className='flex min-h-full items-center justify-center px-1 py-4 text-center md:p-4'>
            <DialogPanel className='animate-popupFadeIn w-full max-w-[370px]! scale-90 transform space-y-4 rounded-xl border bg-white p-0! opacity-0 transition-all duration-500 ease-out'>
              <div className='relative'>
                <Button
                  center
                  onClick={handleClose}
                  className={cn(
                    'absolute -top-10 -right-0.5 z-20 h-[35px]! w-[35px]! cursor-pointer rounded-md bg-[#5B5C60] pt-0! pr-0! pb-[1px]! pl-[1px]! text-[#fff] hover:bg-[#424244]',
                  )}
                >
                  <IoCloseSharp fill='white' size={23} />
                </Button>
              </div>
              <Container className='p-2.5'>
                <XImage
                  width={342}
                  height={145}
                  src={data?.product?.thumbnail?.publicLink}
                  alt={data?.title}
                  className='max-h-[145px] w-full rounded-[10px] object-cover object-top'
                />
              </Container>
              <Container className='relative flex-col px-4'>
                <Text
                  semiBold
                  size={SIZE_ENUM.XL}
                  className='text-center leading-[38px] text-[#424244]'
                >
                  {data?.title}
                </Text>
                <Text className='line-clamp-2 text-start text-[13px] leading-[25px] text-[#76767C] md:line-clamp-4'>
                  {data?.description}
                </Text>
              </Container>
              <When
                condition={
                  data?.showDescription && data?.product?.price && data?.product?.price > 0
                }
              >
                <Container className='relative mt-3 mr-[18px] ml-4 flex h-[52px] items-center justify-end rounded-[6px] bg-gradient-to-tr from-[rgba(255,218,162,0.4)] to-[rgba(255,243,224,0.4)] py-2 pr-[15px] pl-2.5'>
                  <PriceView
                    lastPriceClassName='text-start mr-0.5 -mb-2! text-[15px]! tracking-[-0.13em]'
                    discountLabelClassName=' text-[13px]! tracking-[-0.12em]! absolute right-[15px] top-[15%] mx-auto border-[#FFFFFF] border-[2px] w-9 h-9 shadow-[0px_4px_12px_-5px_#FF7B016E]'
                    showDiscountLabel={true}
                    key={data?.id}
                    price={data?.product?.price}
                    lastPrice={data?.product?.lastPrice}
                    discount={data?.product?.discount}
                    currencyClassName='text-[12px] text-[#BFBFBF]'
                    priceWrapperClassName='flex gap-[5px]'
                    priceClassName='text-[23px]! -ml-0.5 tracking-[-0.13em]'
                  />
                </Container>
              </When>
              <Container
                className={clsx(
                  data?.product?.price ? 'justify-between' : 'justify-center',
                  'relative flex flex-row items-center pt-3 pb-4',
                  // keep the button row's outer edges aligned with the orange price box above;
                  // gap-2.5 (10px) + flex-1 buttons: fixed 10px gap, buttons fill the rest.
                  data?.showDescription
                    ? 'mr-[18px] ml-4 gap-2.5'
                    : 'mr-8! ml-1 gap-2 pr-4 pl-2 md:mx-0! md:gap-2 md:pl-4',
                )}
              >
                <If condition={data?.showDescription}>
                  <Then>
                    <a
                      href={previewLink}
                      target='_blank'
                      rel='noopener noreferrer'
                      className='flex h-[52px] min-w-0 flex-1 cursor-pointer items-center justify-center rounded-lg bg-[#F4F5F6]! px-2 text-[13px] font-semibold whitespace-nowrap! text-[#878F9B] transition-colors! duration-500 hover:border-1 hover:border-[#878F9B] hover:bg-[#878F9B]! hover:text-[#F4F5F6] md:px-4 md:text-[15px]'
                    >
                      {localization.viewProduct}
                    </a>
                  </Then>
                  <Else>
                    <When condition={data?.product?.price}>
                      <Container
                        className={clsx(
                          data?.product?.discount &&
                            data?.product?.discount > 0 &&
                            'mr-3! pr-1.5 md:mr-9!',
                          'relative w-fit',
                        )}
                      >
                        <PriceView
                          lastPriceClassName='text-start mr-0.5 -mb-2!'
                          discountLabelClassName=' text-[13px]! absolute -right-10  top-[15%] mx-auto border-[#FFFFFF] border-[2px] w-9 h-9 shadow-[0px_4px_12px_-5px_#FF7B016E]'
                          showDiscountLabel={true}
                          key={data?.id}
                          price={data?.product?.price}
                          lastPrice={data?.product?.lastPrice}
                          discount={data?.product?.discount}
                          currencyClassName='text-[11px]'
                        />
                      </Container>
                    </When>
                  </Else>
                </If>

                <Button
                  className={clsx(
                    'h-[52px] min-w-0 flex-1 bg-[#FFB616]! px-2 text-[13px] transition-colors! duration-500 hover:border-1 hover:border-[#F89406] hover:bg-[#FFF]! hover:text-[#F89406] md:px-4 md:text-[15px]',
                    !data?.showDescription && 'md:min-w-[160px] md:flex-none',
                  )}
                  onClick={handleAddToCartClick}
                  isLoading={isAddingToCart}
                >
                  {data?.buttonText}
                </Button>
              </Container>
            </DialogPanel>
          </div>
        </div>
      </Dialog>
    </>
  );
}
